import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { languageContent } from '../utils/languageContent';
import { shareLearningProgress } from '../utils/shareUtils';
import { supabase } from '../supabase';
import { isGuestUser } from '../utils/guestUtils';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';

interface User {
  id: string;
  email: string;
  full_name?: string;
}

interface ProfileScreenProps {
  user: User | null;
  onLogout: () => void;
}

interface UserStats {
  streak: number;
  xp: number;
  lessonsCompleted: number;
  totalLessons: number;
}

const ProfileScreen: React.FC<ProfileScreenProps> = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const content = languageContent[currentLanguage];
  const [stats, setStats] = useState<UserStats>({
    streak: 0,
    xp: 0,
    lessonsCompleted: 0,
    totalLessons: 0
  });
  const [emailVerified, setEmailVerified] = useState<boolean>(false);
  const [isGuest, setIsGuest] = useState<boolean>(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedName, setEditedName] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [userDisplayName, setUserDisplayName] = useState<string>('');

  useEffect(() => {
    loadStats();
    checkEmailVerification();
    setIsGuest(isGuestUser());
    loadUserDisplayName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadUserDisplayName = async () => {
    if (isGuestUser()) {
      setUserDisplayName('Guest User');
      setEditedName('Guest User');
      return;
    }

    try {
      // First try to get from database (most up-to-date)
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        const { data: dbUser } = await supabase
          .from('users')
          .select('display_name, full_name')
          .eq('id', authUser.id)
          .single();

        const displayName = dbUser?.display_name || 
                           dbUser?.full_name || 
                           authUser.user_metadata?.full_name ||
                           authUser.user_metadata?.name ||
                           authUser.email?.split('@')[0] || 
                           'User';

        setUserDisplayName(displayName);
        setEditedName(displayName);
      } else {
        // Fallback to user prop
        const fallbackName = user?.full_name || user?.email?.split('@')[0] || 'User';
        setUserDisplayName(fallbackName);
        setEditedName(fallbackName);
      }
    } catch (error) {
      console.error('Error loading user display name:', error);
      // Fallback to user prop
      const fallbackName = user?.full_name || user?.email?.split('@')[0] || 'User';
      setUserDisplayName(fallbackName);
      setEditedName(fallbackName);
    }
  };

  const checkEmailVerification = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setEmailVerified(user.email_confirmed_at !== null);
        if (!user.email_confirmed_at) {
          setShowVerificationMessage(true);
        }
      }
    } catch (error) {
      console.error('Error checking email verification:', error);
    }
  };

  const handleResendVerification = async () => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user?.email || ''
      });
      
      if (error) {
        alert('Error sending verification email: ' + error.message);
      } else {
        alert('Verification email sent! Please check your inbox.');
      }
    } catch (error) {
      console.error('Error resending verification:', error);
      alert('Error sending verification email. Please try again.');
    }
  };

  const handleSaveProfile = async () => {
    if (isGuest) {
      alert('Guest users cannot edit profile. Please create an account first.');
      return;
    }

    if (!editedName.trim()) {
      alert('Name cannot be empty');
      return;
    }

    setSaving(true);
    try {
      // Update user metadata in Supabase Auth
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          full_name: editedName.trim()
        }
      });

      if (updateError) {
        alert('Error updating profile: ' + updateError.message);
        setSaving(false);
        return;
      }

      // Also update in public.users table
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        const { error: dbError } = await supabase
          .from('users')
          .update({
            display_name: editedName.trim(),
            full_name: editedName.trim()
          })
          .eq('id', currentUser.id);

        if (dbError) {
          console.error('Error updating database:', dbError);
        }
      }

      setIsEditing(false);
      // Reload the display name from database
      await loadUserDisplayName();
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const loadStats = () => {
    try {
      const progressKey = 'userProgress';
      const existingProgress = localStorage.getItem(progressKey);
      const progress = existingProgress ? JSON.parse(existingProgress) : {
        completedLessons: [],
        totalXP: 0,
        streak: 0
      };

      setStats({
        streak: progress.streak || 0,
        xp: progress.totalXP || 0,
        lessonsCompleted: progress.completedLessons?.length || 0,
        totalLessons: 25 // This would come from database
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };


  return (
    <div className="profile-screen">
      <div className="screen-header">
        <button 
          className="header-btn back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="header-center">
          <h1>{content.profile}</h1>
        </div>
        <button 
          className="header-btn settings-btn"
          onClick={() => navigate('/settings')}
        >
          ⋯
        </button>
      </div>

      <LessonBannerAd />

      <div className="profile-content">
        {/* Profile Header Card - Redesigned */}
        <div className="profile-header-card">
          <div className="profile-header-content">
          <div className="profile-avatar-large">
              {(isEditing ? editedName : (userDisplayName || user?.full_name || user?.email || 'U')).charAt(0).toUpperCase()}
            </div>
            
            <div className="profile-info-section">
              {isEditing ? (
                <div style={{ width: '100%' }}>
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    placeholder="Enter your name"
                    className="profile-edit-input"
                    autoFocus
                  />
                  <div className="profile-edit-buttons">
                    <button
                      onClick={handleSaveProfile}
                      disabled={saving}
                      className="profile-save-btn"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        setEditedName(userDisplayName || user?.full_name || user?.email?.split('@')[0] || '');
                      }}
                      disabled={saving}
                      className="profile-cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h2>{userDisplayName || user?.full_name || user?.email?.split('@')[0] || 'User'}</h2>
                  <p className="profile-email">{user?.email}</p>
                  
                  {/* Email Verification Status */}
                  {!isGuest && (
                    <div style={{ marginTop: '8px' }}>
                      {emailVerified ? (
                        <div style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          gap: '4px',
                          padding: '6px 12px',
                          background: '#e0e5ec',
                          color: '#28a745',
                          borderRadius: '16px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          boxShadow: 'inset 2px 2px 4px rgba(163, 177, 198, 0.4), inset -2px -2px 4px rgba(255, 255, 255, 0.8)'
                        }}>
                          <span>✓</span>
                          <span>Email Verified</span>
                        </div>
                      ) : (
                        <div style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          gap: '4px',
                          padding: '6px 12px',
                          background: '#e0e5ec',
                          color: '#ffc107',
                          borderRadius: '16px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          boxShadow: 'inset 2px 2px 4px rgba(163, 177, 198, 0.4), inset -2px -2px 4px rgba(255, 255, 255, 0.8)'
                        }}>
                          <span>⚠️</span>
                          <span>Email Not Verified</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Guest Mode Badge */}
                  {isGuest && (
                    <div style={{ 
                      marginTop: '8px',
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '4px',
                      padding: '6px 12px',
                      background: '#e0e5ec',
                      color: '#667eea',
                      borderRadius: '16px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      boxShadow: 'inset 2px 2px 4px rgba(163, 177, 198, 0.4), inset -2px -2px 4px rgba(255, 255, 255, 0.8)'
                    }}>
                      <span>👤</span>
                      <span>Guest Mode</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          
          {/* Action Buttons Section - Separated from profile info */}
          <div className="profile-actions-section">
            {!isEditing && (
              <>
                {isGuest ? (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Clear guest flag and force navigation
                      localStorage.removeItem('isGuest');
                      // Use window.location to ensure navigation works
                      setTimeout(() => {
                        window.location.href = '/register';
                      }, 100);
                    }}
                    className="profile-action-btn primary"
                  >
                    <span className="btn-icon">✨</span>
                    <span className="btn-text">Create Account to Save Progress</span>
                    <span className="btn-arrow">→</span>
                  </button>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="profile-action-btn secondary"
                  >
                    <span className="btn-icon">✏️</span>
                    <span className="btn-text">Edit Profile</span>
                    <span className="btn-arrow">→</span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Email Verification Message */}
        {!isGuest && showVerificationMessage && !emailVerified && (
          <div style={{
            margin: '15px 20px',
            padding: '15px',
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            fontSize: '0.9rem'
          }}>
            <p style={{ margin: '0 0 10px 0', fontWeight: '600', color: '#856404' }}>
              ⚠️ Please verify your email address
            </p>
            <p style={{ margin: '0 0 10px 0', color: '#856404', fontSize: '0.85rem' }}>
              We sent a verification email to <strong>{user?.email}</strong>. Please check your inbox and click the verification link.
            </p>
            <button
              onClick={handleResendVerification}
              style={{
                padding: '8px 16px',
                background: '#ffc107',
                color: '#000',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}
            >
              Resend Verification Email
            </button>
          </div>
        )}

        {/* Stats Grid */}
        <div className="profile-stats-grid">
          <div className="profile-stat-card">
            <div className="stat-icon">🔥</div>
            <div className="stat-value">{stats.streak}</div>
            <div className="stat-label">Day Streak</div>
          </div>
          <div className="profile-stat-card">
            <div className="stat-icon">⭐</div>
            <div className="stat-value">{stats.xp}</div>
            <div className="stat-label">Total XP</div>
          </div>
          <div className="profile-stat-card">
            <div className="stat-icon">📚</div>
            <div className="stat-value">{stats.lessonsCompleted}</div>
            <div className="stat-label">Lessons</div>
          </div>
          <div className="profile-stat-card">
            <div className="stat-icon">🎯</div>
            <div className="stat-value">{Math.floor(stats.xp / 100) + 1}</div>
            <div className="stat-label">Level</div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="profile-actions-compact">
          <button 
            className="profile-btn share-btn" 
            onClick={() => shareLearningProgress(stats.streak, stats.xp, stats.lessonsCompleted)}
            aria-label="Share your learning progress"
          >
            <span className="btn-icon">🔗</span>
            <span className="btn-text">Share Progress</span>
            <span className="btn-arrow">→</span>
          </button>
          
          <button className="profile-btn" onClick={() => navigate('/settings')}>
            <span className="btn-icon">⚙️</span>
            <span className="btn-text">Settings</span>
            <span className="btn-arrow">→</span>
          </button>
          
          <button className="profile-btn logout-btn" onClick={onLogout}>
            <span className="btn-icon">🚪</span>
            <span className="btn-text">Sign Out</span>
            <span className="btn-arrow">→</span>
          </button>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default ProfileScreen;
